import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Loader2, CheckCircle2, XCircle, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  suggestHubHandle,
  validateHubDisplayName,
  validateHubHandle,
} from "@/lib/hub/hubIdentity";
import { HubAvatar } from "@/components/social/HubAvatar";
import { useCreateHubSettings, useHubSettings, useHubHandleCheck } from "@/hooks/hub/useHubSettings";
import { useAuth } from "@/contexts/AuthContext";
import { hubHandleReservedExempt } from "@/lib/hub/hubIdentity";

/**
 * First-time LR Hub onboarding — guidelines acceptance + display name / handle choice.
 * Writes only to `hub_settings`. Mounted from SocialHubPage only.
 */

const GUIDELINES = [
  "Keep it professional — this is a freight industry network, not a rumor board.",
  "No harassment, threats, discrimination or targeted attacks on companies or people.",
  "No posting of another party's private contact, rate or dispatch details.",
  "No spam, unsolicited sales blasts or repeated identical posts.",
  "Do not impersonate LaneRunner staff, another company or another member.",
];

export function HubOnboardingModal() {
  const { data: settings, isLoading } = useHubSettings();
  const create = useCreateHubSettings();
  const check = useHubHandleCheck();

  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [handleEdited, setHandleEdited] = useState(false);
  const [showRole, setShowRole] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);

  const nameError = useMemo(
    () => (displayName ? validateHubDisplayName(displayName) : null),
    [displayName],
  );
  // Reserved words (lanerunner/admin/official/...) are bypassed for LaneRunner
  // admins and the founder account — mirrors the server-side guard.
  const { isAdmin, profile } = useAuth();
  const allowReserved = hubHandleReservedExempt(isAdmin, profile?.email);
  const handleError = useMemo(
    () => (handle ? validateHubHandle(handle, allowReserved) : null),
    [handle, allowReserved],
  );

  // Suggest a handle from the display name until the member edits it themselves.
  useEffect(() => {
    if (handleEdited) return;
    setHandle(suggestHubHandle(displayName));
  }, [displayName, handleEdited]);

  // Debounced availability check against the Hub-only RPC.
  useEffect(() => {
    setAvailable(null);
    if (!handle || handleError) return;
    const t = setTimeout(() => {
      check.mutateAsync(handle).then(setAvailable).catch(() => setAvailable(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, handleError]);

  const open = !isLoading && !settings;
  const canSubmit =
    accepted &&
    !!displayName &&
    !nameError &&
    !!handle &&
    !handleError &&
    available === true &&
    !create.isPending;

  const submit = async () => {
    try {
      await create.mutateAsync({ displayName, handle, showRole });
      toast.success("Welcome to LR Hub");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not save your Hub profile.";
      // Lost a race for the handle — force a fresh availability check.
      if (/just taken/i.test(msg)) setAvailable(false);
      toast.error(msg);
    }

  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-brand-teal-deep" />
            Set up your LR Hub profile
          </DialogTitle>
          <DialogDescription className="text-xs">
            Your Hub identity is separate from your LaneRunner account. Pick the name members see
            on your posts plus a unique @handle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <HubAvatar username={displayName || handle || "LR"} size="lg" />
            <div className="min-w-0 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground">
                {displayName || "Your generated avatar"}
                {handle ? <span className="ml-1.5 font-normal">@{handle}</span> : null}
              </p>
              <p className="mt-0.5">
                Everyone starts with a generated initials avatar. You can upload a photo later in
                LR Hub Settings — it shows publicly only after review.
              </p>
            </div>
          </div>

          {/* Privacy tip */}
          <div className="flex items-start gap-2.5 rounded-md border-l-4 border-l-brand-red bg-danger-subtle p-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
            <p className="text-xs leading-relaxed text-brand-red">
              <span className="font-semibold">Want to stay private?</span> You don't have to use your
              real name or company — pick a made-up username to keep your identity private. Something
              like <span className="font-medium">truckerguy1</span> works great. Keep it clean and
              professional.
            </p>
          </div>

          {/* Display name */}
          <div className="space-y-1.5">
            <Label htmlFor="hub-display-name" className="text-xs font-semibold uppercase tracking-wide">
              Display name
            </Label>
            <Input
              id="hub-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Mike Jones"
              maxLength={30}
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">
              {nameError ?? "Spaces and capitals are fine. 2–30 characters. This is what shows on your posts."}
            </p>
          </div>

          {/* Handle */}
          <div className="space-y-1.5">
            <Label htmlFor="hub-handle" className="text-xs font-semibold uppercase tracking-wide">
              Handle
            </Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                @
              </span>
              <Input
                id="hub-handle"
                value={handle}
                onChange={(e) => {
                  setHandleEdited(true);
                  setHandle(e.target.value.replace(/\s/g, "").toLowerCase());
                }}
                placeholder="mikejones"
                maxLength={20}
                autoComplete="off"
                className="pl-7 pr-9"
              />
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                {check.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : available === true ? (
                  <CheckCircle2 className="h-4 w-4 text-brand-teal-deep" />
                ) : available === false || handleError ? (
                  <XCircle className="h-4 w-4 text-destructive" />
                ) : null}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {handleError
                ? handleError
                : available === false
                  ? "That handle is taken."
                  : "Letters, numbers, underscores. No spaces. This is your unique @handle — changeable once every 30 days."}
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div className="min-w-0 pr-3">
              <p className="text-xs font-semibold text-foreground">Show my role</p>
              <p className="text-[11px] text-muted-foreground">
                Display Shipper / Broker / Carrier next to your name.
              </p>
            </div>
            <Switch checked={showRole} onCheckedChange={setShowRole} />
          </div>

          <div className="rounded-lg border border-border p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
              Community guidelines
            </p>
            <ul className="mt-2 space-y-1.5">
              {GUIDELINES.map((g) => (
                <li key={g} className="flex gap-2 text-[11px] leading-snug text-muted-foreground">
                  <span className="mt-[5px] h-1 w-1 flex-shrink-0 rounded-full bg-brand-teal-deep" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-[11.5px] font-medium text-foreground">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(v === true)}
                className="mt-0.5"
              />
              <span>I've read and agree to follow the LR Hub guidelines.</span>
            </label>
          </div>

          <Button onClick={submit} disabled={!canSubmit} className="w-full">
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Enter LR Hub
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HubOnboardingModal;
